import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Instagram, Menu, X, Info } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const GOLD = "#C9A227";
const BLACK = "#0D0D0D";
const CREAM = "#FAF6EC";
const NAME = "C & F Dental Care Clinic";
const FB = "https://www.facebook.com/candfdentalcare/";
const IG = "https://www.instagram.com/candfdental/";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-70px" }, transition: { duration: 0.6, ease: "easeOut" } };

export default function CfConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Leadership", "#leadership"], ["Services", "#services"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#1C1A14", fontFamily: "'Manrope', sans-serif" }} data-testid="cf-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Manrope:wght@400;500;600;700&display=swap'); .cf-serif{font-family:'Playfair Display',serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b" style={{ borderColor: "#EDE3C8" }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="cf-logo" className="flex items-center gap-3">
            <img src="/dental/cf-dental-concept/logo.png" alt={NAME} className="h-11 w-11 object-contain" />
            <span className="cf-serif text-xl" style={{ color: BLACK }}>C &amp; F Dental</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#4A4438] hover:text-black transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="cf-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5" style={{ background: GOLD }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="cf-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — signage photo, gold/black premium */}
      <section className="relative min-h-[86vh] flex items-end" data-testid="cf-hero">
        <img src="/dental/cf-dental-concept/signage.jpg" alt="C & F Dental Care Clinic — Smile Professionals" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(13,13,13,.72) 0%, rgba(13,13,13,.58) 48%, rgba(13,13,13,.08) 82%), linear-gradient(0deg, rgba(13,13,13,.55), transparent 55%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 w-full">
          <motion.div {...fadeUp} className="max-w-2xl border-l-4 pl-5 sm:pl-7" style={{ borderColor: GOLD }}>
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD }}>Capitol Site, Cebu City</span>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl cf-serif text-white leading-[1.05]">Smile Professionals</h1>
            <p className="mt-5 text-lg text-white/75 max-w-xl">General and orthodontic dental care led by Dr. Chariz T. Asoy and Dr. Felix Trinidad Asoy.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#visit" data-testid="cf-hero-book" className="px-8 py-3.5 text-sm font-semibold text-black" style={{ background: GOLD }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="cf-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
            <p className="mt-7 text-sm text-white/60">4.7★ Rating · 53 Reviews · 205 Facebook Recommends</p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl cf-serif leading-relaxed">
          C &amp; F Dental Care Clinic provides general and orthodontic dentistry from a Capitol Site clinic trusted across Cebu City.
        </motion.p>
      </section>

      {/* LEADERSHIP */}
      <section id="leadership" className="py-20" style={{ background: BLACK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>Leadership</span>
            <h2 className="mt-3 text-4xl cf-serif text-white">Dr. Chariz T. Asoy &amp; Dr. Felix Trinidad Asoy</h2>
            <p className="mt-5 text-white/65 leading-relaxed">Dr. Chariz leads the clinic as CEO. Dr. Felix serves as COO and oversees oral surgery cases, including extractions and post-operative care.</p>
          </motion.div>
          <motion.img {...fadeUp} src="/dental/cf-dental-concept/team-collage.jpg" alt="The C & F Dental Care team" className="w-full object-cover" />
        </div>
      </section>

      {/* MYOBRACE FEATURE (their own real graphic) */}
      <section id="services" className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
        <motion.div {...fadeUp} className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "#8A7326" }}>Our Services</span>
          <h2 className="mt-3 text-4xl cf-serif">Myobrace &amp; Orthodontic Care</h2>
        </motion.div>
        <motion.img {...fadeUp} src="/dental/cf-dental-concept/myobrace-molds.jpg" alt="Myobrace orthodontic appliances used at C & F Dental Care" className="w-full max-w-2xl mx-auto object-contain shadow-md" />
        <div className="mt-12 grid sm:grid-cols-4 gap-6 text-center">
          {["Myobrace / Orthodontics", "General Dentistry", "Cosmetic Dentistry", "Oral Surgery"].map((s) => (
            <div key={s} className="py-6 border-t-2" style={{ borderColor: GOLD }}>
              <p className="font-semibold text-sm">{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REAL RESULT */}
      <section className="py-24" style={{ background: "#F2E9D2" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2 {...fadeUp} className="text-4xl cf-serif mb-10">A Smile Transformed</motion.h2>
          <motion.img {...fadeUp} src="/dental/cf-dental-concept/before-after.jpg" alt="A real restoration case shared by C & F Dental Care" className="w-full max-w-sm mx-auto object-contain shadow-lg" />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
        <motion.h2 {...fadeUp} className="text-4xl cf-serif text-center mb-14">What Patients Say</motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { text: "I'd had bad extractions elsewhere before. Dr. Felix explained the procedure clearly and this one was unusually fast and painless.", author: "M." },
            { text: "Exceptionally friendly staff. Dr. Felix thoroughly explained my wisdom-tooth extraction and aftercare, and Dr. Cha was skilled during stitch removal.", author: "R." },
            { text: "Thank you Dr. Chariz and staff — her calming manner really helped a nervous patient like me relax.", author: "J." },
          ].map((q, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`cf-testimonial-${i}`} className="p-7 bg-white border-t-2" style={{ borderColor: GOLD }}>
              <p className="text-[#3A362C]/85 leading-relaxed">"{q.text}"</p>
              <p className="mt-4 font-semibold text-sm" style={{ color: "#8A7326" }}>{q.author}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-24" style={{ background: BLACK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl cf-serif text-white">Visit Us</h2>
            <ul className="mt-8 space-y-5 text-white/70">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: GOLD }} className="mt-0.5" />G/F TLT Building, corner Don Gil Garcia &amp; Don Julio Llorente St., Capitol Site, Cebu City</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: GOLD }} className="mt-0.5" /><a href="tel:+63322342233" className="hover:underline">(032) 234-2233</a> · 0977 238 6355</li>
              <li className="flex items-start gap-4"><Mail size={20} style={{ color: GOLD }} className="mt-0.5" /><a href="mailto:candfdentalcare@gmail.com" className="hover:underline">candfdentalcare@gmail.com</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: GOLD }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="cf-facebook-link" className="hover:underline">candfdentalcare</a></li>
              <li className="flex items-start gap-4"><Instagram size={20} style={{ color: GOLD }} className="mt-0.5" /><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="cf-instagram-link" className="hover:underline">candfdental</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px]">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("TLT Building Don Gil Garcia Capitol Site Cebu City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-white border-t" style={{ borderColor: "#EDE3C8" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#6B6350]">
          <p className="cf-serif text-base" style={{ color: BLACK }}>C &amp; F Dental Care Clinic</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-black shadow-xl" style={{ background: GOLD }}>Message C&amp;F Dental</a>
      <ConceptCTAPill slug="cf-dental-concept" />
    </div>
  );
}
